import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import {
  Key, ShieldCheck, Loader2, AlertCircle, CheckCircle2, ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import GastifyBrand from '../components/public/GastifyBrand';

/**
 * /login/reset
 * -------------
 * Página de aterrizaje para:
 *   1. Reset de contraseña (link de "Olvidé mi contraseña")
 *   2. Magic link de invitación (?invited=1 — los nuevos miembros llegan acá
 *      a setear su contraseña por primera vez)
 *
 * Supabase setea automáticamente la sesión cuando el usuario llega desde
 * estos links, así que basta con llamar updateUser.
 */
export default function LoginReset() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isInvitation, setIsInvitation] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setIsInvitation(params.get('invited') === '1');

    // Esperar a que Supabase parsee el hash/access_token y nos dé la sesión
    let timeoutId;
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        setReady(true);
      } else {
        timeoutId = setTimeout(checkSession, 250);
      }
    };
    checkSession();

    return () => clearTimeout(timeoutId);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    if (password.length < 6) {
      setFeedback({ type: 'error', message: 'La contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    if (password !== confirmPassword) {
      setFeedback({ type: 'error', message: 'Las contraseñas no coinciden.' });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      // limpiar marca de "requires_pw_update" si existe
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          for (const table of ['profiles', 'users']) {
            await supabase.from(table).update({ requires_pw_update: false }).eq('id', user.id).then(() => {}).catch(() => {});
          }
        }
      } catch { /* no crítico */ }

      toast.success(isInvitation ? '¡Bienvenido! Tu cuenta quedó activa.' : 'Contraseña actualizada.');
      navigate('/sistema', { replace: true });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err?.message || 'No pudimos actualizar la contraseña. Pedí un nuevo link.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--gst-cream-50)]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="animate-spin text-[var(--gst-mint-600)]" />
          <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--gst-graphite-500)]">
            Validando link...
          </p>
        </div>
      </div>
    );
  }

  const inputCls = `w-full rounded-2xl border bg-[var(--surface)] px-4 py-3.5 pl-12 text-sm font-semibold text-[var(--gst-graphite-900)] outline-none transition placeholder:text-[var(--gst-graphite-300)] ${
    feedback?.type === 'error'
      ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100'
      : 'border-[var(--gst-border-strong)] focus:border-[var(--gst-mint-400)] focus:ring-2 focus:ring-[var(--gst-mint-100)]'
  }`;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--gst-cream-50)] p-4">
      <div className="mb-6">
        <GastifyBrand textTone="dark" iconSize={44} />
      </div>

      <div className="w-full max-w-md bg-[var(--surface)] rounded-3xl border border-[var(--gst-border-strong)] shadow-xl p-8">
        <div className="text-[11px] font-black uppercase tracking-[0.22em] text-[var(--gst-graphite-700)] mb-2">
          {isInvitation ? 'Activar cuenta' : 'Reset de contraseña'}
        </div>
        <h1 className="text-2xl font-black text-[var(--gst-graphite-900)] mb-2">
          {isInvitation ? 'Bienvenido a Gastify' : 'Nueva contraseña'}
        </h1>
        <p className="text-sm text-[var(--gst-graphite-700)] mb-6 leading-relaxed">
          {isInvitation
            ? 'Definí una contraseña para tu cuenta y entrá al sistema.'
            : 'Ingresá una contraseña nueva. La vieja queda invalidada.'}
        </p>

        {feedback && (
          <div className={`mb-5 flex items-start gap-2 rounded-xl p-3 text-[13px] font-semibold ${
            feedback.type === 'error'
              ? 'bg-red-50 border border-red-100 text-red-600'
              : 'bg-[var(--gst-mint-100)] border border-[var(--gst-mint-200)] text-[var(--gst-mint-700)]'
          }`}>
            {feedback.type === 'error'
              ? <AlertCircle size={16} className="shrink-0 mt-0.5" />
              : <CheckCircle2 size={16} className="shrink-0 mt-0.5" />}
            <span>{feedback.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-700)] mb-2">
              Nueva contraseña
            </label>
            <div className="relative">
              <Key size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-500)]" />
              <input
                required
                type="password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className={inputCls}
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-700)] mb-2">
              Confirmar contraseña
            </label>
            <div className="relative">
              <ShieldCheck size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-500)]" />
              <input
                required
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repetí la contraseña"
                className={inputCls}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-black uppercase tracking-[0.12em] transition ${
              submitting
                ? 'cursor-not-allowed bg-[var(--gst-mint-300)] text-white/80'
                : 'bg-[var(--gst-mint-500)] text-white hover:bg-[var(--gst-mint-600)]'
            }`}
          >
            {submitting
              ? <><Loader2 size={17} className="animate-spin" /> Procesando</>
              : <>{isInvitation ? 'Entrar al sistema' : 'Guardar nueva contraseña'} <ChevronRight size={16} /></>
            }
          </button>
        </form>
      </div>
    </div>
  );
}
